import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { NotificationManager } from "react-notifications";
import {
  Headphones,
  Clock,
  Target,
  TrendingUp,
  Users,
  UserCheck,
  UserX,
  Pause,
  Layers,
  RefreshCw,
  PhoneIncoming,
  Timer,
} from "lucide-react";
import SiteWrapper from "../../../../SiteWrapper.react";
import { Crud_Catalogos } from "../../../../herramientas/Crud_Catalogos";
import {
  PROVEEDOR,
  fmtNumero,
  fmtDuracion,
  fmtPorcentaje,
  normalizar,
} from "./dashboardUtils";
import "./dashboard.css";

const INTERVALO_MS = 10000; // refresco automático cada 10s

/* Color del estado de un agente */
const COLOR_ESTADO = {
  available: "green",
  busy: "red",
  paused: "amber",
  ringing: "blue",
  wrapup: "blue",
  offline: "",
};

const DashboardColasPage = () => {
  const [queues, setQueues] = useState([]);
  const [agents, setAgents] = useState([]);
  const [global, setGlobal] = useState(null);
  const [ultimaAct, setUltimaAct] = useState(null);
  const [cargandoInicial, setCargandoInicial] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const cargar = useCallback((silencioso = true) => {
    Crud_Catalogos(
      `softphone_v3/dashboard?provedor=${PROVEEDOR}&tipo=wallboard`,
      "softphone",
      "get",
      "",
      []
    )
      .then((res) => {
        const data = normalizar(res) || {};
        setQueues(Array.isArray(data.queues) ? data.queues : []);
        setAgents(Array.isArray(data.agents) ? data.agents : []);
        setGlobal(data.global_stats || null);
        setUltimaAct(new Date());
      })
      .catch(() => {
        if (!silencioso) NotificationManager.error("Error al cargar el wallboard de colas");
      })
      .finally(() => setCargandoInicial(false));
  }, []);

  useEffect(() => {
    cargar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => cargar(true), INTERVALO_MS);
    return () => clearInterval(t);
  }, [autoRefresh, cargar]);

  /* ---------- KPIs globales ---------- */
  const kpis = useMemo(() => {
    const g = global || {};
    return [
      {
        label: "Agentes totales",
        value: fmtNumero(g.total_agents),
        icon: <Users size={22} />,
        accent: "#4f46e5",
        sub: `${fmtNumero(queues.length)} colas`,
      },
      {
        label: "Disponibles",
        value: fmtNumero(g.available_agents),
        icon: <UserCheck size={22} />,
        accent: "#10b981",
        sub: "Listos para atender",
      },
      {
        label: "Ocupados / Pausa",
        value: `${fmtNumero(g.busy_agents)} / ${fmtNumero(g.paused_agents)}`,
        icon: <Pause size={22} />,
        accent: "#f59e0b",
        sub: "En llamada / pausados",
      },
      {
        label: "En espera",
        value: fmtNumero(g.total_calls_waiting),
        icon: <Clock size={22} />,
        accent: (g.total_calls_waiting || 0) > 0 ? "#ef4444" : "#64748b",
        sub: `Espera máx. ${fmtDuracion(g.longest_wait)}`,
        live: true,
      },
      {
        label: "En curso",
        value: fmtNumero(g.total_calls_in_progress),
        icon: <PhoneIncoming size={22} />,
        accent: "#06b6d4",
        sub: "Llamadas activas",
        live: true,
      },
      {
        label: "SLA global",
        value: fmtPorcentaje(g.overall_sla),
        icon: <Target size={22} />,
        accent: "#8b5cf6",
        sub: `${fmtNumero(g.today_answered)} atendidas hoy`,
      },
    ];
  }, [global, queues]);

  /* ---------- Agentes por estado (resumen) ---------- */
  const resumenEstados = useMemo(() => {
    const r = { available: 0, busy: 0, paused: 0, ringing: 0, wrapup: 0, offline: 0 };
    agents.forEach((a) => {
      const st = (a.status || "offline").toLowerCase();
      if (r[st] !== undefined) r[st] += 1;
      else r.offline += 1;
    });
    return r;
  }, [agents]);

  /* Dona: distribución de agentes por estado */
  const donaEstados = useMemo(() => {
    const r = resumenEstados;
    return {
      series: [r.available, r.busy, r.paused, r.ringing + r.wrapup],
      options: {
        chart: { type: "donut", fontFamily: "inherit" },
        labels: ["Disponibles", "Ocupados", "En pausa", "Otros"],
        colors: ["#10b981", "#ef4444", "#f59e0b", "#06b6d4"],
        legend: { position: "bottom", fontSize: "13px" },
        dataLabels: { enabled: true, formatter: (v) => `${v.toFixed(0)}%` },
        stroke: { width: 2 },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Agentes",
                  fontSize: "13px",
                  color: "#64748b",
                  formatter: () => fmtNumero(agents.length),
                },
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 1400,
            options: {
              chart: { height: 280 },
              legend: { position: "bottom", fontSize: "12px" },
            },
          },
        ],
      },
    };
  }, [resumenEstados, agents]);

  /* Solo colas con actividad (agentes o llamadas) para la gráfica/tabla principal */
  const colasActivas = useMemo(
    () =>
      [...queues]
        .filter(
          (q) =>
            (q.total_agents || 0) > 0 ||
            (q.today_calls || 0) > 0 ||
            (q.calls_waiting || 0) > 0 ||
            (q.calls_in_progress || 0) > 0
        )
        .sort((a, b) => (b.total_agents || 0) - (a.total_agents || 0)),
    [queues]
  );

  /* Barras: agentes por cola (disponibles/ocupados/pausa) */
  const barrasAgentes = useMemo(() => {
    const data = colasActivas.slice(0, 12);
    return {
      hayDatos: data.length > 0,
      series: [
        { name: "Disponibles", data: data.map((q) => Number(q.available_agents) || 0) },
        { name: "Ocupados", data: data.map((q) => Number(q.busy_agents) || 0) },
        { name: "En pausa", data: data.map((q) => Number(q.paused_agents) || 0) },
      ],
      options: {
        chart: { type: "bar", stacked: true, fontFamily: "inherit", toolbar: { show: false } },
        colors: ["#10b981", "#ef4444", "#f59e0b"],
        plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: "62%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: data.map((q) => q.queue_name || "-"),
          labels: { style: { colors: "#94a3b8" } },
        },
        yaxis: { labels: { style: { colors: "#64748b", fontSize: "12px" } } },
        grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
        legend: { position: "top", horizontalAlign: "right" },
        tooltip: { y: { formatter: (v) => fmtNumero(v) + " agentes" } },
      },
    };
  }, [colasActivas]);

  const estadoAgenteChip = (status) => {
    const st = (status || "offline").toLowerCase();
    const color = COLOR_ESTADO[st] !== undefined ? COLOR_ESTADO[st] : "";
    const etiquetas = {
      available: "Disponible",
      busy: "Ocupado",
      paused: "En pausa",
      ringing: "Timbrando",
      wrapup: "Wrap-up",
      offline: "Desconectado",
    };
    return <span className={`dash-chip ${color}`}>{etiquetas[st] || status || "-"}</span>;
  };

  const slaChip = (valor, meta) => {
    const v = Number(valor) || 0;
    const m = Number(meta) || 0;
    if (m > 0 && v >= m) return <span className="dash-chip green">{fmtPorcentaje(v)}</span>;
    if (v >= 80) return <span className="dash-chip amber">{fmtPorcentaje(v)}</span>;
    return <span className="dash-chip red">{fmtPorcentaje(v)}</span>;
  };

  /* Agentes agrupados: mostramos solo los activos (available/busy/paused primero) */
  const agentesOrdenados = useMemo(() => {
    const prioridad = { busy: 0, ringing: 1, paused: 2, wrapup: 3, available: 4, offline: 5 };
    return [...agents].sort((a, b) => {
      const pa = prioridad[(a.status || "offline").toLowerCase()] ?? 9;
      const pb = prioridad[(b.status || "offline").toLowerCase()] ?? 9;
      return pa - pb;
    });
  }, [agents]);

  return (
    <SiteWrapper>
      <div className="dash-wrap">
        {/* Encabezado */}
        <div className="dash-header">
          <div>
            <h1>
              <Headphones size={22} style={{ verticalAlign: "-4px", marginRight: 8 }} />
              Wallboard de Colas
            </h1>
            <div className="dash-subtitle">
              Estado en vivo de colas y agentes
              {ultimaAct ? ` · Actualizado ${ultimaAct.toLocaleTimeString("es-MX")}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="dash-btn ghost" onClick={() => setAutoRefresh((v) => !v)}>
              {autoRefresh ? <Pause size={16} /> : <RefreshCw size={16} />}
              {autoRefresh ? "Auto: ON" : "Auto: OFF"}
            </button>
            <button className="dash-btn" onClick={() => cargar(false)}>
              <RefreshCw size={16} />
              Actualizar
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="dash-kpis">
          {kpis.map((k, i) => (
            <div className="kpi-card" key={i} style={{ "--kpi-accent": k.accent }}>
              <div className="kpi-icon" style={{ background: k.accent }}>
                {k.icon}
              </div>
              <div className="kpi-label">{k.label}</div>
              <div className="kpi-value">{k.value}</div>
              <div className="kpi-sub">
                {k.live ? <span className="live-dot" /> : null}
                {k.sub}
              </div>
            </div>
          ))}
        </div>

        {cargandoInicial ? (
          <div className="panel">
            <div className="dash-spinner" />
            <div className="dash-empty">Cargando wallboard…</div>
          </div>
        ) : (
          <>
            {/* Agentes por cola + distribución de estados */}
            <div className="dash-grid" style={{ marginBottom: 18 }}>
              <div className="panel col-8">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">Agentes por cola</h3>
                    <div className="panel-desc">Disponibilidad por cola activa</div>
                  </div>
                </div>
                {barrasAgentes.hayDatos ? (
                  <ReactApexChart
                    options={barrasAgentes.options}
                    series={barrasAgentes.series}
                    type="bar"
                    height={Math.min(420, Math.max(300, barrasAgentes.series[0].data.length * 34))}
                  />
                ) : (
                  <div className="dash-empty">Sin colas con agentes asignados.</div>
                )}
              </div>

              <div className="panel col-4 panel-center">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">Estado de agentes</h3>
                    <div className="panel-desc">Distribución actual</div>
                  </div>
                </div>
                <div className="panel-body-center">
                  {agents.length ? (
                    <ReactApexChart
                      options={donaEstados.options}
                      series={donaEstados.series}
                      type="donut"
                      height={300}
                    />
                  ) : (
                    <div className="dash-empty">Sin agentes.</div>
                  )}
                </div>
              </div>
            </div>

            {/* Tabla de colas activas */}
            <div className="dash-grid" style={{ marginBottom: 18 }}>
              <div className="panel col-12">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <Layers size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                      Colas activas
                    </h3>
                    <div className="panel-desc">
                      Mostrando {colasActivas.length} de {queues.length} colas con actividad
                    </div>
                  </div>
                </div>
                {colasActivas.length ? (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Cola</th>
                        <th>Ext.</th>
                        <th>Agentes</th>
                        <th>Disp.</th>
                        <th>Ocup.</th>
                        <th>Pausa</th>
                        <th>En espera</th>
                        <th>En curso</th>
                        <th>Hoy</th>
                        <th>SLA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {colasActivas.map((q, i) => (
                        <tr key={q.queue_id || i}>
                          <td>
                            <strong>{q.queue_name || "-"}</strong>
                          </td>
                          <td>{q.queue_extension || "-"}</td>
                          <td>{fmtNumero(q.total_agents)}</td>
                          <td>
                            <span className="dash-chip green">{fmtNumero(q.available_agents)}</span>
                          </td>
                          <td>{fmtNumero(q.busy_agents)}</td>
                          <td>{fmtNumero(q.paused_agents)}</td>
                          <td>
                            {(q.calls_waiting || 0) > 0 ? (
                              <span className="dash-chip red">{fmtNumero(q.calls_waiting)}</span>
                            ) : (
                              fmtNumero(q.calls_waiting)
                            )}
                          </td>
                          <td>{fmtNumero(q.calls_in_progress)}</td>
                          <td>{fmtNumero(q.today_calls)}</td>
                          <td>{slaChip(q.current_sla, q.sla_target)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="dash-empty">No hay colas con actividad en este momento.</div>
                )}
              </div>
            </div>

            {/* Agentes conectados */}
            <div className="dash-grid">
              <div className="panel col-12">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <UserCheck size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                      Agentes conectados
                    </h3>
                    <div className="panel-desc">{fmtNumero(agents.length)} agentes en colas</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span className="dash-chip green">{resumenEstados.available} disp.</span>
                    <span className="dash-chip red">{resumenEstados.busy} ocup.</span>
                    <span className="dash-chip amber">{resumenEstados.paused} pausa</span>
                  </div>
                </div>
                {agentesOrdenados.length ? (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Agente</th>
                        <th>Ext.</th>
                        <th>Cola / Equipo</th>
                        <th>Estado</th>
                        <th>Llamadas hoy</th>
                        <th>
                          <Timer size={13} style={{ verticalAlign: "-1px" }} /> Talk hoy
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentesOrdenados.map((a, i) => (
                        <tr key={`${a.agent_id || a.extension}-${a.team_id || i}`}>
                          <td>
                            <Users
                              size={13}
                              style={{ verticalAlign: "-1px", marginRight: 6, opacity: 0.5 }}
                            />
                            {a.agent_name || "-"}
                          </td>
                          <td>{a.extension || "-"}</td>
                          <td className="text-truncate">{a.team_name || "-"}</td>
                          <td>{estadoAgenteChip(a.status)}</td>
                          <td>{fmtNumero(a.today_calls)}</td>
                          <td>{fmtDuracion(a.today_talk_time)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="dash-empty">
                    <UserX size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
                    <div>No hay agentes conectados.</div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </SiteWrapper>
  );
};

export default DashboardColasPage;
